import { useDisclosure } from '@chakra-ui/react';
import { Modal, ModalCloseButton, ModalContent, ModalOverlay } from '@chakra-ui/react';
import { SlackIcon } from '@sketchnote/icons';
import { useRouter } from 'next/router';
import React, { useEffect, useState } from 'react';
import { API } from 'src/api';

interface IntegrationType {
  channel: string;
  srcType: string;
  _id: string;
  src: string;
  title: string;
}

export const SlackPage = ({ onClose }: { onClose: () => void }) => {
  const [integrations, setIntegrations] = useState<IntegrationType[]>([]);
  const [isLoading, setLoader] = useState<boolean>(true);

  async function fetchSlackIntegrations() {
    try {
      const response = await API.get('/integrations?type=slack');
      setLoader(false);
      if (response.data.success) {
        return setIntegrations(response.data.data);
      }
    } catch (error) {
      console.error(error);
      return null;
    }
    return null;
  }

  const filterRemovedIntegration = (id) => {
    setIntegrations((prevInt) => prevInt.filter((i) => i._id !== id));
  };

  useEffect(() => {
    fetchSlackIntegrations();
  }, []);

  return (
    <div className="overflow-y-auto px-48" style={{ height: '59rem' }}>
      <section className="py-48 pb-28">
        <div className="flex">
          <SlackIcon wrapperClass="pr-16" size={46} />
          <div>
            <div className="mb-10">
              <div className="font-medium">Slack</div>
              <div className="text-sm">Communication</div>
            </div>
          </div>
        </div>
      </section>
      <section>
        <div className="font-medium mb-10">Connected files ({integrations.length})</div>
        <div className="flex py-12 px-20">
          <span className="w-1/3">Title</span>
          <span className="w-1/3">Slack Channel</span>
        </div>
        <div className="pb-48">
          {integrations.length === 0 && isLoading && (
            <span className="flex border rounded-md py-12 px-20 animate-pulse bg-gray-300">
              &nbsp;
            </span>
          )}
          {integrations.length === 0 && !isLoading && (
            <span className="flex border rounded-md py-12 px-20">No integrations found</span>
          )}
          {integrations.map((item) => (
            <IntegrationTile
              key={item._id}
              item={item}
              onClose={onClose}
              filterRemovedIntegration={filterRemovedIntegration}
            />
          ))}
        </div>
      </section>
    </div>
  );
};

const IntegrationTile = ({
  item,
  onClose,
  filterRemovedIntegration,
}: {
  item: IntegrationType;
  onClose: () => void;
  filterRemovedIntegration: (id) => void;
}) => {
  const router = useRouter();
  const { isOpen, onOpen, onClose: onModalClose } = useDisclosure();

  const handlePath = () => {
    switch (item.srcType) {
      case 'board':
        router.push(`/boards/${item.src}`);
        break;
      case 'docs':
        router.push(`/docs/${item.src}`);
        break;
    }
    onClose();
  };

  const handleChannelRemoval = async () => {
    const response = await API.delete(`integrations?type=slack&id=${item._id}`);
    if (response.data.success) {
      filterRemovedIntegration(item._id);
    }
    onModalClose();
  };

  return (
    <>
      <div className="flex border rounded-md py-12 px-20 mb-8">
        <span className="w-1/3 font-medium truncate pr-6">{item.title}</span>
        <span className="w-1/3 truncate pr-6">{item.channel}</span>
        <div className="w-1/3 flex justify-around">
          <button onClick={handlePath} className="text-primary-500 font-medium">
            Open
          </button>
          <button onClick={onOpen} className="text-red-800 font-medium">
            Disconnect
          </button>
        </div>
      </div>
      <Modal isOpen={isOpen} onClose={onModalClose}>
        <ModalOverlay />

        <ModalContent
          style={{
            maxWidth: '46rem',
            padding: '32px',
            marginTop: '20rem',
            borderRadius: '8px',
          }}
        >
          <ModalCloseButton />
          <p className="mb-32 text-md text-gray-999">
            Are you sure you want to remove the Slack integration?
          </p>
          <div className="flex justify-end">
            <button
              onClick={onModalClose}
              className="border-blue-500 text-blue-500 border rounded-sm px-24 py-13 mr-8"
            >
              Cancel
            </button>
            <button
              className="border-red-700 text-red-700 border rounded-sm px-24 py-13"
              onClick={handleChannelRemoval}
            >
              Remove
            </button>
          </div>
        </ModalContent>
      </Modal>
    </>
  );
};
